import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import { MaskedContracts, MaskedDiscount, MaskedFeeSchedule, MaskedTeam } from "work/_shared/masked-fields";
import {
  validate,
  validateJSON,
  WorkAgreementJSON,
  WorkAgreementJSONZ,
  WorkAgreementParams,
  WorkAgreementZ,
} from "work/work-agreements/_schemas/work-agreement-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeamSchema from "work/work-agreements/teams/client-team/_schema/client-team-schema";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeamSchema from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import { z } from "zod";

export default class WorkAgreementSchema {
  public deserialize(json: WorkAgreementJSON): WorkAgreementParams {
    const parsed = parseOrThrow(WorkAgreementJSONZ, json, {
      payloadSample: json,
      schemaName: "WorkAgreement",
      operation: "deserialize",
      logError: true,
    });
    return this.codec.decode(parsed);
  }

  public serialize(params: WorkAgreementParams): WorkAgreementJSON {
    const parsed = validate(params);
    return this.codec.encode(parsed);
  }

  private readonly codec = z.codec<typeof WorkAgreementJSONZ, typeof WorkAgreementZ>(
    WorkAgreementJSONZ,
    WorkAgreementZ,
    {
      decode: (value, _payload) => {
        const data = validateJSON(value);

        let clientTeam;
        let vendorTeam;
        let feeSchedule;
        let clientPolicies;
        let vendorPolicies;
        let conflicts;

        if (MaskedTeam.isNotMasked(data.clientTeam)) {
          clientTeam = data.clientTeam ? ClientTeam.fromJSON(data.clientTeam) ?? undefined : undefined;
        } else {
          clientTeam = new MaskedTeam();
        }

        const additionalVendors = data.additionalVendors
          ? EntityMember.fromJSONArray({ jsonArray: data.additionalVendors }) ?? undefined
          : undefined;

        if (MaskedTeam.isNotMasked(data.vendorTeam)) {
          vendorTeam = data.vendorTeam ? VendorTeam.fromJSON(data.vendorTeam) ?? undefined : undefined;
        } else {
          vendorTeam = new MaskedTeam();
        }

        if (MaskedFeeSchedule.isNotMasked(data.feeSchedule)) {
          feeSchedule = data.feeSchedule
            ? (FeeScheduleCategory.fromJSONArray(data.feeSchedule.categories ?? []) as
              | FeeScheduleCategory[]
              | undefined)
            : undefined;
        } else {
          feeSchedule = new MaskedFeeSchedule();
        }

        if (MaskedContracts.isNotMasked(data.clientPolicies)) {
          clientPolicies = (() => {
            const docs = [];
            for (const doc of data.clientPolicies?.documents ?? []) {
              if (!doc.documentId) continue;
              docs.push(
                WorkDocument.fromJSON({ ...doc.documentInfo, id: doc.documentId, replacesId: doc.replacesId })
              );
            }
            return docs;
          })();
        } else {
          clientPolicies = new MaskedContracts();
        }

        if (MaskedContracts.isNotMasked(data.vendorPolicies)) {
          vendorPolicies = (() => {
            const docs = [];
            for (const doc of data.vendorPolicies?.documents ?? []) {
              if (!doc.documentId) continue;
              docs.push(
                WorkDocument.fromJSON({ ...doc.documentInfo, id: doc.documentId, replacesId: doc.replacesId })
              );
            }
            return docs;
          })();
        } else {
          vendorPolicies = new MaskedContracts();
        }

        if (MaskedContracts.isNotMasked(data.conflicts)) {
          conflicts = (() => {
            const docs = [];
            for (const doc of data.conflicts?.documents ?? []) {
              if (!doc.documentId) continue;
              docs.push(
                WorkDocument.fromJSON({ ...doc.documentInfo, id: doc.documentId, replacesId: doc.replacesId })
              );
            }
            return docs;
          })();
        } else {
          conflicts = new MaskedContracts();
        }

        return {
          id: Guid.fromJSON(data.id) ?? undefined,
          RFPId: Guid.fromJSON(data.RFPId, { allowEmpty: true }) ?? undefined,
          name: data.name ? ProjectName.fromJSON(data.name) ?? undefined : undefined,
          description: data.description ? ProjectDescription.fromJSON(data.description) ?? undefined : undefined,
          clientTeam: clientTeam,
          additionalVendors: additionalVendors,
          vendorTeam: vendorTeam,
          teamRestricted: MaskedTeam.isNotMasked(data.teamRestricted)
            ? new AHBoolean(data.teamRestricted?.value ?? false)
            : new MaskedTeam(),
          feeSchedule: feeSchedule ?? [],
          startDate: data.startDate === null ? null : (AHDate.fromJSON(data.startDate) ?? undefined),
          endDate: data.endDate === null ? null : (AHDate.fromJSON(data.endDate) ?? undefined),
          discount: data.discount?.isMasked ? new MaskedDiscount() : Percent.fromJSON(data.discount?.percent ?? 0),
          clientPolicies: clientPolicies,
          vendorPolicies: vendorPolicies,
          conflictsCheckWaived: MaskedContracts.isMasked(data.conflictsCheckWaived)
            ? new MaskedContracts()
            : new AHBoolean(data.conflictsCheckWaived?.value ?? false),
          conflicts: conflicts,
          clientTeamTemplateIds:
            data.clientTeamTemplateIds?.map((id) => Guid.fromJSON(id)).filter((id) => !isNullOrUndefined(id)) ?? [],
          vendorTeamTemplateIds:
            data.vendorTeamTemplateIds?.map((id) => Guid.fromJSON(id)).filter((id) => !isNullOrUndefined(id)) ?? [],
          clientFeeScheduleTemplateIds:
            data.clientFeeScheduleTemplateIds?.map((id) => Guid.fromJSON(id)).filter((id) => !isNullOrUndefined(id)) ??
            [],
          vendorFeeScheduleTemplateIds:
            data.vendorFeeScheduleTemplateIds?.map((id) => Guid.fromJSON(id)).filter((id) => !isNullOrUndefined(id)) ??
            [],
          hireDate: AHDate.fromJSON(data.hireDate) ?? undefined,
          archiveDate: AHDate.fromJSON(data.archiveDate) ?? undefined,
        };
      },
      encode: (value, _payload) => {
        const agreement = validate(value);

        let clientTeam;
        let vendorTeam;
        let feeSchedule;
        let clientPolicies;
        let vendorPolicies;
        let conflicts;

        if (MaskedTeam.isNotMasked(agreement.clientTeam)) {
          clientTeam = agreement.clientTeam
            ? {
              isMasked: false,
              ...ClientTeamSchema.serialize(agreement.clientTeam),
            }
            : undefined;
        } else {
          clientTeam = { isMasked: true };
        }

        if (MaskedTeam.isNotMasked(agreement.vendorTeam)) {
          vendorTeam = agreement.vendorTeam
            ? {
              isMasked: false,
              ...VendorTeamSchema.serialize(agreement.vendorTeam),
            }
            : undefined;
        } else {
          vendorTeam = { isMasked: true };
        }

        if (MaskedFeeSchedule.isNotMasked(agreement.feeSchedule)) {
          feeSchedule = agreement.feeSchedule?.map((fs) => FeeScheduleCategorySchema.serialize(fs));
        }

        if (MaskedContracts.isNotMasked(agreement.clientPolicies)) {
          clientPolicies = {
            isMasked: false,
            documents: agreement.clientPolicies?.map((doc) => ({
              documentId: doc.id?.toJSON?.() ?? doc.id,
              documentInfo: doc.toJSON(),
              replacesId: doc.replacesId?.toJSON?.() ?? (doc.replacesId?.value ?? null),
            })) ?? []
          };
        } else {
          clientPolicies = { isMasked: true };
        }

        if (MaskedContracts.isNotMasked(agreement.vendorPolicies)) {
          vendorPolicies = {
            isMasked: false,
            documents: agreement.vendorPolicies?.map((doc) => ({
              documentId: doc.id?.toJSON?.() ?? doc.id,
              documentInfo: doc.toJSON(),
              replacesId: doc.replacesId?.toJSON?.() ?? (doc.replacesId?.value ?? null),
            })) ?? []
          };
        } else {
          vendorPolicies = { isMasked: true };
        }

        if (MaskedContracts.isNotMasked(agreement.conflicts)) {
          conflicts = {
            isMasked: false,
            documents: agreement.conflicts?.map((doc) => ({
              documentId: doc.id?.toJSON?.() ?? doc.id,
              documentInfo: doc.toJSON(),
              replacesId: doc.replacesId?.toJSON?.() ?? (doc.replacesId?.value ?? null),
            })) ?? []
          }
        } else {
          conflicts = { isMasked: true };
        }

        return {
          id: agreement.id?.toJSON(),
          RFPId: agreement.RFPId?.toJSON(),
          name: agreement.name?.toJSON(),
          description: agreement.description?.toJSON(),
          clientTeam: clientTeam,
          additionalVendors: agreement.additionalVendors?.map((vendor) => vendor.toJSON()) ?? [],
          vendorTeam: vendorTeam,
          teamRestricted: MaskedTeam.isNotMasked(agreement.teamRestricted)
            ? { isMasked: false, value: agreement.teamRestricted?.toJSON() ?? false }
            : { isMasked: true },
          feeSchedule: {
            isMasked: MaskedFeeSchedule.isMasked(agreement.feeSchedule),
            categories: MaskedFeeSchedule.isMasked(agreement.feeSchedule) ? undefined : feeSchedule,
          },
          startDate: agreement.startDate === null ? null : agreement.startDate?.toJSON(),
          endDate: agreement.endDate === null ? null : agreement.endDate?.toJSON(),
          discount: MaskedDiscount.isNotMasked(agreement.discount)
            ? { percent: agreement.discount?.toJSON() ?? 0, isMasked: false }
            : { percent: null, isMasked: true },
          clientPolicies: clientPolicies,
          vendorPolicies: vendorPolicies,
          conflictsCheckWaived: MaskedContracts.isNotMasked(agreement.conflictsCheckWaived)
            ? { isMasked: false, value: agreement.conflictsCheckWaived?.toJSON() ?? false }
            : { isMasked: true },
          conflicts: conflicts,
          clientTeamTemplateIds: agreement.clientTeamTemplateIds?.map((id) => id.toJSON()),
          vendorTeamTemplateIds: agreement.vendorTeamTemplateIds?.map((id) => id.toJSON()),
          clientFeeScheduleTemplateIds: agreement.clientFeeScheduleTemplateIds?.map((id) => id.toJSON()),
          vendorFeeScheduleTemplateIds: agreement.vendorFeeScheduleTemplateIds?.map((id) => id.toJSON()),
          hireDate: agreement.hireDate?.toJSON(),
          archiveDate: agreement.archiveDate?.toJSON(),
        };
      },
    }
  );
}
